import ApiSmile from '@/api/api-smile';
import ActionArea from '@/component/action-area';
import {ActionButton} from '@/component/action-area/action-area';
import DataRow from '@/component/data-row';
import PageContainer from '@/component/page-container';
import {RecommendSource, RecommendState} from '@/model/const';
import LabelUtil from '@/util/label-util';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import Utils from '@/util/utils';
import {DatePicker, Form, InputNumber, Modal, Select} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment, {Moment} from 'moment';
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {RecommendData} from '../types';
import './recommend-detail.scss';

interface UpdateData {
    breakevenPrice?: number;
    premiumRate?: number;
    profitRate?: number;
    recommendBuyPrice?: number;
    recommendBuyReason?: string;
    recommendBuyTime?: Moment;
    recommendSellReason?: string;
    recommendSellTime?: Moment;
    recommendSource?: string;
    remainYear?: number;
    residualScale?: number;
    totalMarketValue?: number;
    yieldToMaturity?: number;
}

interface CreateData extends UpdateData {
    code: string;
    name: string;
}

interface InfoBrief {
    id: number;
    code: string;
    name: string;
}

const RecommendDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState<RecommendData>();
    const [isEdit, setIsEdit] = useState(false);
    const [form] = Form.useForm();
    const isNew = !(Number(id) > 0);
    const [infoBrief, setInfoBrief] = useState<InfoBrief[]>([]);

    useEffect(() => {
        if (!Utils.isNaturalNumber(id)) {
            navigate('/', {replace: true});
            return;
        }

        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getRecommendDetail(id || '');
            if (response?.data?.code === 0) {
                setData(response.data.data);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        if (Number(id) > 0) {
            load();
        }
    }, [navigate, id]);

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getInfoBrief();
            if (response?.data?.code === 0) {
                setInfoBrief(response.data.data || []);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        if (isNew) {
            load();
        }
    }, [isNew]);

    useEffect(() => {
        if (isNew) {
            setIsEdit(true);
            form.setFieldsValue({
                recommendBuyTime: moment(),
                recommendSource: RecommendSource.MIN7,
            });
        }
    }, [isNew, form]);

    const onResetEdit = () => {
        setIsEdit(false);
        form.resetFields();
    };

    const onCancelClick = () => {
        if (isNew) {
            navigate('/recommend', {replace: true});
        } else {
            onResetEdit();
        }
    };

    const validateUpdateData = (payload: UpdateData) => {
        if (!payload.recommendSource) {
            MessageUtil.error('推荐来源不能为空');
            return false;
        }
        if (!payload.recommendBuyTime) {
            MessageUtil.error('推荐买入时间不能为空');
            return false;
        }
        if (!Number.isFinite(payload.recommendBuyPrice)) {
            MessageUtil.error('推荐买入价格格式错误');
            return false;
        }
        if (!((payload.recommendBuyPrice || 0) > 0)) {
            MessageUtil.error('推荐买入价格不能小于0');
            return false;
        }
        if (data?.recommendState === RecommendState.SELL && !payload.recommendSellTime) {
            MessageUtil.error('推荐卖出时间不能为空');
            return false;
        }
        return true;
    };

    const getUpdateData = (value: any) => {
        return {
            recommendSource: value.recommendSource || '',
            recommendBuyTime: value.recommendBuyTime,
            recommendBuyPrice: Number(value.recommendBuyPrice) || 0,
            breakevenPrice: Number(value.breakevenPrice) || 0,
            premiumRate: Number(value.premiumRate) || 0,
            remainYear: Number(value.remainYear) || 0,
            residualScale: Number(value.residualScale) || 0,
            totalMarketValue: Number(value.totalMarketValue) || 0,
            yieldToMaturity: Number(value.yieldToMaturity) || 0,
            recommendBuyReason: value.recommendBuyReason || '',
            recommendSellTime: value.recommendSellTime,
            profitRate: Number(value.profitRate) || 0,
            recommendSellReason: value.recommendSellReason || '',
        };
    };

    const getConfirmContent = (payload: UpdateData, create?: CreateData) => {
        const isSell = data?.recommendState === RecommendState.SELL;
        return (
            <div>
                {create?.code && <div>{`代码：${create.code}`}</div>}
                {create?.name && <div>{`名称：${create.name}`}</div>}
                <div>{`来源：${LabelUtil.getRecommendSource(payload.recommendSource)}`}</div>
                <div>{`买入时间：${payload.recommendBuyTime?.format('YYYY-MM-DD') || '--'}`}</div>
                <div>{`买入价格：${Utils.formatPrice(payload.recommendBuyPrice)}`}</div>
                <div>{`保本极限价：${Utils.formatPrice(payload.breakevenPrice)}`}</div>
                <div>{`转股溢价率：${Utils.formatPercentage(payload.premiumRate)}`}</div>
                <div>{`剩余年限：${payload.remainYear}`}</div>
                <div>{`剩余规模：${payload.residualScale}亿元`}</div>
                <div>{`总市值：${payload.totalMarketValue}亿元`}</div>
                <div>{`到期收益率：${Utils.formatPercentage(payload.yieldToMaturity)}`}</div>
                <div>{`买入原因：${payload.recommendBuyReason}`}</div>
                {isSell && <div>{`卖出时间：${payload.recommendSellTime?.format('YYYY-MM-DD') || '--'}`}</div>}
                {isSell && <div>{`卖出收益率：${Utils.formatPercentage(payload.profitRate)}`}</div>}
                {isSell && <div>{`卖出原因：${payload.recommendSellReason}`}</div>}
            </div>
        );
    };

    const onCreateData = () => {
        const value = form.getFieldsValue() || {};
        const payload: CreateData = {
            code: value.code || '',
            name: infoBrief.find((i) => i.code === value.code)?.name || '',
            ...getUpdateData(value),
        };
        if (!(payload.code && payload.name)) {
            MessageUtil.error('请选择名称和代码');
            return;
        }
        if (!validateUpdateData(payload)) {
            return;
        }

        const create = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.createRecommendBuy(payload);
            if (response?.data?.code === 0) {
                navigate(`/recommend/${response.data.data.id}`, {replace: true});
                onResetEdit();
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定保存吗？',
            content: getConfirmContent(payload, payload),
            onOk: create,
        });
    };

    const onUpdateData = () => {
        const value = form.getFieldsValue() || {};
        const payload: UpdateData = getUpdateData(value);
        if (!validateUpdateData(payload)) {
            return;
        }

        const update = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.updateRecommendDetail(id || '', payload);
            if (response?.data?.code === 0) {
                setData(response.data.data);
                onCancelClick();
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定更新吗？',
            content: getConfirmContent(payload),
            onOk: update,
        });
    };

    const onSaveClick = () => {
        if (isNew) {
            onCreateData();
        } else {
            onUpdateData();
        }
    };

    const onEditClick = () => {
        setIsEdit(true);
        form.setFieldsValue({
            ...data,
            recommendBuyTime: moment(data?.recommendBuyTime || 0),
            recommendSellTime: moment(data?.recommendSellTime || 0),
        });
    };

    const getButtons = () => {
        const buttons: ActionButton[] = [];
        if (isEdit) {
            buttons.push({text: '取消', onClick: onCancelClick});
            buttons.push({text: '保存', onClick: onSaveClick, type: 'primary'});
        } else {
            if (data) {
                buttons.push({text: '编辑', onClick: onEditClick});
            }
        }
        return buttons;
    };

    return (
        <PageContainer className="page-recommend-detail">
            <ActionArea buttons={getButtons()} />
            <Form form={form}>
                <DataRow title="名称代码">
                    {isEdit && isNew ? (
                        <Form.Item name={'code'}>
                            <Select showSearch optionFilterProp="children">
                                {infoBrief.map((item) => (
                                    <Select.Option key={item.code} value={item.code}>
                                        {`${item.name}(${item.code})`}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : (
                        `${data?.name || ''}(${data?.code || ''})`
                    )}
                </DataRow>
                <DataRow title="推荐状态">{isNew ? '--' : LabelUtil.getRecommendState(data?.recommendState || '')}</DataRow>
                <DataRow title="推荐来源">
                    {isEdit ? (
                        <Form.Item name={'recommendSource'}>
                            <Select optionFilterProp="children">
                                <Select.Option value={RecommendSource.MIN7}>7分钟</Select.Option>
                                <Select.Option value={RecommendSource.SELF}>自选</Select.Option>
                            </Select>
                        </Form.Item>
                    ) : (
                        LabelUtil.getRecommendSource(data?.recommendSource || '')
                    )}
                </DataRow>
                <DataRow title="推荐买入时间">
                    {isEdit ? (
                        <Form.Item name={'recommendBuyTime'}>
                            <DatePicker />
                        </Form.Item>
                    ) : (
                        Utils.formatDate(data?.recommendBuyTime)
                    )}
                </DataRow>
                <DataRow title="推荐买入价格">
                    {isEdit ? (
                        <Form.Item name={'recommendBuyPrice'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} min={0} />
                        </Form.Item>
                    ) : (
                        Utils.formatPrice(data?.recommendBuyPrice)
                    )}
                </DataRow>
                <DataRow title="保本极限价">
                    {isEdit ? (
                        <Form.Item name={'breakevenPrice'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} min={0} />
                        </Form.Item>
                    ) : (
                        Utils.formatPrice(data?.breakevenPrice)
                    )}
                </DataRow>
                <DataRow title="转股溢价率">
                    {isEdit ? (
                        <Form.Item name={'premiumRate'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} addonAfter="%" />
                        </Form.Item>
                    ) : (
                        Utils.formatPercentage(data?.premiumRate)
                    )}
                </DataRow>
                <DataRow title="剩余年限">
                    {isEdit ? (
                        <Form.Item name={'remainYear'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} />
                        </Form.Item>
                    ) : (
                        data?.remainYear || 0
                    )}
                </DataRow>
                <DataRow title="剩余规模">
                    {isEdit ? (
                        <Form.Item name={'residualScale'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} addonAfter="亿元" />
                        </Form.Item>
                    ) : (
                        `${data?.residualScale || 0}亿元`
                    )}
                </DataRow>
                <DataRow title="总市值">
                    {isEdit ? (
                        <Form.Item name={'totalMarketValue'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} addonAfter="亿元" />
                        </Form.Item>
                    ) : (
                        `${data?.totalMarketValue || 0}亿元`
                    )}
                </DataRow>
                <DataRow title="到期收益率">
                    {isEdit ? (
                        <Form.Item name={'yieldToMaturity'}>
                            <InputNumber className="data-desc price-input" stringMode step={'0.01'} addonAfter="%" />
                        </Form.Item>
                    ) : (
                        Utils.formatPercentage(data?.yieldToMaturity)
                    )}
                </DataRow>
                <DataRow title="买入原因">
                    {isEdit ? (
                        <Form.Item name={'recommendBuyReason'}>
                            <TextArea rows={4} className="data-desc" />
                        </Form.Item>
                    ) : (
                        data?.recommendBuyReason || ''
                    )}
                </DataRow>
                <DataRow title="推荐卖出时间">
                    {data?.recommendState === RecommendState.SELL ? (
                        isEdit ? (
                            <Form.Item name={'recommendSellTime'}>
                                <DatePicker />
                            </Form.Item>
                        ) : (
                            Utils.formatDate(data?.recommendSellTime)
                        )
                    ) : (
                        '--'
                    )}
                </DataRow>
                <DataRow title="卖出收益率">
                    {data?.recommendState === RecommendState.SELL ? (
                        isEdit ? (
                            <Form.Item name={'profitRate'}>
                                <InputNumber className="data-desc price-input" stringMode step={'0.01'} addonAfter="%" />
                            </Form.Item>
                        ) : (
                            Utils.formatPercentage(data?.profitRate)
                        )
                    ) : (
                        '--'
                    )}
                </DataRow>
                <DataRow title="卖出原因">
                    {data?.recommendState === RecommendState.SELL ? (
                        isEdit ? (
                            <Form.Item name={'recommendSellReason'}>
                                <TextArea rows={4} className="data-desc" />
                            </Form.Item>
                        ) : (
                            data?.recommendSellReason || ''
                        )
                    ) : (
                        '--'
                    )}
                </DataRow>
            </Form>
        </PageContainer>
    );
};

export default RecommendDetail;
